// src/marketing/content/useGenerations.ts
import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { Generation, GenerationAspectRatio, GenerationResolution } from '../../lib/types'
import { isSettled, nextPollDelay, POLL_MAX_DURATION_MS } from './generations'

export const generationKeys = {
  list: (contentId: string) => ['generations', contentId] as const,
  detail: (id: string) => ['generation', id] as const,
}

export function useGenerations(contentId: string | null) {
  return useQuery<Generation[], Error>({
    queryKey: generationKeys.list(contentId ?? ''),
    queryFn: () =>
      api.get<{ generations: Generation[] }>(`/api/contents/${contentId}/generations`).then((r) => r.generations),
    enabled: Boolean(contentId),
    staleTime: 1000 * 30,
  })
}

export type CreateGenerationInput = {
  prompt: string
  duration: number
  resolution: GenerationResolution
  aspectRatio: GenerationAspectRatio
  generateAudio: boolean
  referenceAttachmentIds: string[]
}

export function useCreateGeneration(contentId: string) {
  const queryClient = useQueryClient()
  return useMutation<Generation, Error, CreateGenerationInput>({
    mutationFn: (body) =>
      api.post<{ generation: Generation }>(`/api/contents/${contentId}/generations`, body).then((r) => r.generation),
    onSuccess: (created) => {
      queryClient.setQueryData<Generation[]>(generationKeys.list(contentId), (prev) => [created, ...(prev ?? [])])
    },
  })
}

type PollResponse = { generation: Generation; polled: boolean; pollError?: string }

/** Vrai quand plus de POLL_MAX_DURATION_MS se sont écoulées depuis la création (faux si la date est illisible). */
function isPastDeadline(createdAt: string, now: number = Date.now()): boolean {
  const created = Date.parse(createdAt)
  return Number.isFinite(created) && now - created > POLL_MAX_DURATION_MS
}

/** Active mais hors délai : c'est le cas qui produit timedOut. */
function isExpired(g: Generation | null): boolean {
  return g !== null && !isSettled(g) && isPastDeadline(g.createdAt)
}

/**
 * Poll /api/generations/:id avec le backoff Higgsfield tant que la génération n'est pas réglée.
 * Arrêt après POLL_MAX_DURATION_MS depuis la création (timedOut = true).
 *
 * Le délai de refetch est calculé une seule fois par réponse reçue (effet sur dataUpdatedAt) :
 * refetchInterval reste une lecture pure, un re-rendu de l'hôte ne réarme donc pas le timer.
 */
export function useGenerationPolling(generation: Generation | null): { generation: Generation | null; timedOut: boolean } {
  const queryClient = useQueryClient()
  const attemptRef = useRef(0)
  const delayRef = useRef(0)
  const id = generation?.id ?? null

  // Une prop réglée n'a plus rien à attendre du serveur : elle prime sur tout cache détail.
  const needsPoll = generation !== null && !isSettled(generation)

  useEffect(() => {
    attemptRef.current = 0
    delayRef.current = 0
    // Réinitialisation uniquement au changement de génération.
  }, [id])

  const cached = queryClient.getQueryData<PollResponse>(generationKeys.detail(id ?? ''))?.generation ?? null
  const polledSettled = cached !== null && isSettled(cached)
  // timedOut est dérivé de la valeur retournée (jamais stocké) : il retombe à faux dès que la génération est réglée,
  // par exemple après une annulation qui écrit le cache détail. Chaque réponse (succès ou erreur) provoque un rendu.
  const timedOut = isExpired(needsPoll ? (cached ?? generation) : generation)
  const enabled = needsPoll && !polledSettled && !timedOut

  const query = useQuery<PollResponse, Error>({
    queryKey: generationKeys.detail(id ?? ''),
    queryFn: () => api.get<PollResponse>(`/api/generations/${id}`),
    enabled,
    staleTime: 0,
    refetchInterval: (q) => {
      const g = q.state.data?.generation ?? generation
      if (g === null || isSettled(g) || isPastDeadline(g.createdAt)) return false
      return delayRef.current > 0 ? delayRef.current : false
    },
  })

  const polled = query.data?.generation ?? null
  // Lire errorUpdatedAt pendant le rendu est indispensable : TanStack ne re-rend que sur les propriétés lues.
  // Sans cette lecture, un fetch en erreur ne provoquerait aucun rendu, et timedOut resterait faux après la limite.
  const { dataUpdatedAt, errorUpdatedAt } = query

  // Une tentative par réponse (succès ou erreur) : le délai suivant est fixé ici, jamais dans refetchInterval.
  useEffect(() => {
    if (!needsPoll) return
    delayRef.current = nextPollDelay(attemptRef.current)
    attemptRef.current += 1
  }, [id, needsPoll, dataUpdatedAt, errorUpdatedAt])

  useEffect(() => {
    if (!polled || !isSettled(polled)) return
    void queryClient.invalidateQueries({ queryKey: ['attachments', 'content', polled.contentId] })
    void queryClient.invalidateQueries({ queryKey: generationKeys.list(polled.contentId) })
  }, [polled, queryClient])

  const current = needsPoll ? (polled ?? generation) : generation
  return { generation: current, timedOut: isExpired(current) }
}

export function useCancelGeneration(contentId: string) {
  const queryClient = useQueryClient()
  return useMutation<Generation, Error, string>({
    mutationFn: (id) => api.post<{ generation: Generation }>(`/api/generations/${id}/cancel`, {}).then((r) => r.generation),
    onSuccess: (canceled) => {
      queryClient.setQueryData<Generation[]>(generationKeys.list(contentId), (prev) =>
        (prev ?? []).map((g) => (g.id === canceled.id ? canceled : g)))
      queryClient.setQueryData<PollResponse>(generationKeys.detail(canceled.id), { generation: canceled, polled: false })
    },
  })
}
